import React, { useState } from 'react';
import { motion } from 'framer-motion';
import type { GameConfig, PlayerSetup, AIPersonality } from '../../types/bountifulSea';
import { PLAYER_COLORS } from '../../types/bountifulSea';
import {
  DEFAULT_PLAYER_NAMES,
  AI_PLAYER_NAMES,
  MIN_PLAYERS,
  MAX_PLAYERS,
  MIN_STARTING_DICE,
  MAX_STARTING_DICE,
} from '../../data/bountifulSea/gameConstants';

// ═══════════════════════════════════════════════════════════════
// GAME SETUP COMPONENT
// ═══════════════════════════════════════════════════════════════

interface GameSetupProps {
  onStartGame: (config: GameConfig, players: PlayerSetup[]) => void;
}

export const GameSetup: React.FC<GameSetupProps> = ({ onStartGame }) => {
  const [playerCount, setPlayerCount] = useState(2);
  const [startingDice, setStartingDice] = useState(3);
  const [shipwreckRule, setShipwreckRule] = useState<'depthsClaimAll' | 'washAshore'>('depthsClaimAll');

  // Calculate balanced ante: scales with dice count for longer games
  // Formula: (maxPossibleRoll * diceMultiplier) to ensure pot lasts multiple rounds
  // With 3 dice: max roll = 6 + 18 = 24, so ante ~= 20-25 coins per player
  const calculateBalancedAnte = (dice: number) => {
    const maxRoll = 6 + dice * 6; // Tide die (6) + scoundrel dice (all 6s)
    return Math.ceil(maxRoll * 1.5); // 1.5x max roll ensures multiple rounds
  };

  const anteAmount = calculateBalancedAnte(startingDice);

  const [playerConfigs, setPlayerConfigs] = useState<PlayerSetup[]>(() =>
    Array.from({ length: MAX_PLAYERS }, (_, i) => ({
      name: DEFAULT_PLAYER_NAMES[i] || `Player ${i + 1}`,
      isAI: i >= 1, // First player is human by default
      aiPersonality: 'balanced' as AIPersonality,
      color: PLAYER_COLORS[i],
    }))
  );

  const updatePlayerConfig = (index: number, updates: Partial<PlayerSetup>) => {
    setPlayerConfigs((prev) => {
      const newConfigs = [...prev];
      newConfigs[index] = { ...newConfigs[index], ...updates };

      // Update name if switching to/from AI
      if (updates.isAI !== undefined) {
        if (updates.isAI && !newConfigs[index].name.includes('AI')) {
          newConfigs[index].name = AI_PLAYER_NAMES[index] || `AI ${index + 1}`;
        } else if (!updates.isAI && AI_PLAYER_NAMES.includes(newConfigs[index].name)) {
          newConfigs[index].name = DEFAULT_PLAYER_NAMES[index] || `Player ${index + 1}`;
        }
      }

      return newConfigs;
    });
  };

  const handleStartGame = () => {
    const config: GameConfig = {
      playerCount,
      startingDice,
      anteAmount,
      shipwreckRule,
    };

    const players = playerConfigs.slice(0, playerCount);
    onStartGame(config, players);
  };

  // Check if at least one human player
  const hasHumanPlayer = playerConfigs.slice(0, playerCount).some((p) => !p.isAI);

  return (
    <div className="bs-content-box bs-setup">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h2 style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          Set Sail for Fortune
        </h2>
        <p className="bs-flavor-text" style={{ textAlign: 'center', marginBottom: '2rem' }}>
          Configure your voyage into the treacherous waters of chance and deception...
        </p>
      </motion.div>

      {/* Player Count */}
      <motion.div
        className="bs-setup-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <label className="bs-setup-label">Crew Size: {playerCount} Pirates</label>
        <div className="bs-slider-container">
          <input
            type="range"
            className="bs-slider"
            min={MIN_PLAYERS}
            max={MAX_PLAYERS}
            value={playerCount}
            onChange={(e) => setPlayerCount(Number(e.target.value))}
          />
          <div className="bs-slider-value">
            {MIN_PLAYERS} - {MAX_PLAYERS} players
          </div>
        </div>
      </motion.div>

      {/* Player Configurations */}
      <motion.div
        className="bs-setup-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <label className="bs-setup-label">Your Crew</label>
        {Array.from({ length: playerCount }, (_, i) => (
          <div
            key={i}
            className="bs-player-config"
            style={{ borderLeft: `4px solid ${playerConfigs[i].color}` }}
          >
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                type="text"
                className="bs-input"
                value={playerConfigs[i].name}
                onChange={(e) => updatePlayerConfig(i, { name: e.target.value })}
                style={{ flex: 1 }}
              />
              <label className="bs-checkbox-row">
                <input
                  type="checkbox"
                  checked={playerConfigs[i].isAI}
                  onChange={(e) => updatePlayerConfig(i, { isAI: e.target.checked })}
                />
                <span>AI</span>
              </label>
            </div>

            {playerConfigs[i].isAI && (
              <select
                className="bs-select"
                value={playerConfigs[i].aiPersonality}
                onChange={(e) =>
                  updatePlayerConfig(i, {
                    aiPersonality: e.target.value as AIPersonality,
                  })
                }
              >
                <option value="cautious">Cautious - Plays it safe</option>
                <option value="balanced">Balanced - Moderate risk</option>
                <option value="reckless">Reckless - High risk, high reward</option>
              </select>
            )}
          </div>
        ))}
      </motion.div>

      {/* Game Settings */}
      <motion.div
        className="bs-setup-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <label className="bs-setup-label">Starting Scoundrel Dice: {startingDice}</label>
        <div className="bs-slider-container">
          <input
            type="range"
            className="bs-slider"
            min={MIN_STARTING_DICE}
            max={MAX_STARTING_DICE}
            value={startingDice}
            onChange={(e) => setStartingDice(Number(e.target.value))}
          />
          <div className="bs-slider-value">
            More dice = longer games, more bluffing opportunities
          </div>
        </div>
      </motion.div>

      {/* Ante Display (auto-calculated for balance) */}
      <motion.div
        className="bs-setup-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <div style={{
          padding: '1rem',
          background: 'var(--bs-bg-elevated)',
          borderRadius: '8px',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          textAlign: 'center'
        }}>
          <div style={{ color: 'var(--bs-text-muted)', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
            Ante (auto-balanced)
          </div>
          <div style={{ fontFamily: 'var(--bs-font-heading)', fontSize: '1.5rem', color: 'var(--bs-gold)' }}>
            {anteAmount} coins per player
          </div>
          <div style={{ color: 'var(--bs-sea-teal)', fontSize: '1.1rem', marginTop: '0.5rem' }}>
            Starting Pot: {anteAmount * playerCount} coins
          </div>
        </div>
      </motion.div>

      <motion.div
        className="bs-setup-section"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <label className="bs-setup-label">Shipwreck Rule</label>
        <select
          className="bs-select"
          value={shipwreckRule}
          onChange={(e) =>
            setShipwreckRule(e.target.value as 'depthsClaimAll' | 'washAshore')
          }
        >
          <option value="depthsClaimAll">
            Depths Claim All - Eliminated player's stash returns to pot
          </option>
          <option value="washAshore">
            Wash Ashore - Eliminated player keeps their stash as final score
          </option>
        </select>
      </motion.div>

      {/* Start Button */}
      <motion.div
        style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
      >
        <button
          className="bs-btn bs-btn-primary bs-btn-large"
          onClick={handleStartGame}
          disabled={!hasHumanPlayer}
        >
          Set Sail!
        </button>
      </motion.div>

      {!hasHumanPlayer && (
        <p style={{ textAlign: 'center', color: 'var(--bs-danger)', marginTop: '0.5rem' }}>
          At least one player must be human
        </p>
      )}
    </div>
  );
};

export default GameSetup;
